import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RangeDays } from '../lib/api.js';
import { api } from '../lib/api.js';
import { fmt, absTime, SURFACE_LABELS } from '../lib/format.js';
import { Icon } from '../components/Icon.js';
import { GrowthChart } from '../components/GrowthChart.js';
import { EmptyState } from '../components/EmptyState.js';
import { DashboardSkeleton } from '../components/Skeleton.js';

// "סטטיסטיקה" — the two surfaces that match (and beat) bizz-pilot's reports:
//   1. צמיחת קהל       ← bizz-pilot /api/news/growth  (audience growth over time, per-group deltas)
//   2. שידורים אחרונים ← bizz-pilot /api/news/stats   (per-broadcast 👀 views / read counts)
// Real data only (AudienceSnapshot + DeliveryReceipt) with honest empty states — nothing is mocked.

const RANGES: { v: RangeDays; label: string }[] = [
  { v: 30, label: "30 י'" },
  { v: 90, label: "90 י'" },
];

export function StatsPage(): JSX.Element {
  return (
    <div className="content">
      <div className="page-head">
        <div>
          <div className="eyebrow">תובנות</div>
          <h1>סטטיסטיקה</h1>
          <div className="sub">כמה גדל הקהל, וכמה אנשים באמת ראו כל שידור</div>
        </div>
      </div>

      <GrowthSection />

      <div className="section-title" style={{ marginTop: 30 }}>
        <h2 style={{ fontSize: 20 }}>📊 שידורים אחרונים</h2><span className="rule" />
      </div>
      <p className="stats-sub">
        צפיות בקבוצות WhatsApp ולאיזה יעדים נשלחה כל הודעה. הנתונים נשמרים 7 ימים ומתעדכנים אוטומטית ברקע.
      </p>
      <BroadcastsSection />
    </div>
  );
}

function GrowthSection(): JSX.Element {
  const [range, setRange] = useState<RangeDays>(90);
  const { data, isLoading } = useQuery({ queryKey: ['statsGrowth', range], queryFn: () => api.statsGrowth(range) });

  if (isLoading || !data) return <DashboardSkeleton />;

  const deltaUp = data.delta14d >= 0;
  const maxSize = Math.max(1, ...data.groups.map((g) => g.size));

  return (
    <div className="card growth" style={{ padding: 24 }}>
      <div className="top">
        <div className="headline">
          <div className="eyebrow">צמיחת קהל · {range} יום</div>
          <div className="big">
            <span className="num-sheen sheen-host sheen-once">{fmt(data.total)}</span>
            <small> חברים ב-{fmt(data.groupCount)} קבוצות</small>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className={`delta ${deltaUp ? 'up' : 'dn'}`}>
              <Icon name="analytics" /> ‎{deltaUp ? '+' : ''}{fmt(data.delta14d)}
              {data.delta14dPct !== 0 && <> ({deltaUp ? '+' : ''}{data.delta14dPct}%)</>}
            </span>
            <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>ב-14 הימים האחרונים</span>
          </div>
        </div>
        <div className="range-tabs">
          {RANGES.map((r) => (
            <button key={r.v} className={range === r.v ? 'on' : ''} onClick={() => setRange(r.v)}>{r.label}</button>
          ))}
        </div>
      </div>

      {data.series.length < 2 ? (
        <EmptyState
          icon="analytics"
          title="עוקבים אחרי הצמיחה מהיום"
          sub="הגרף נבנה מצילום יומי של גודל כל קבוצה. הנקודה הראשונה כבר נרשמה — קו הצמיחה יתחיל להיווצר ממחר, ותוך כמה ימים תראה כאן מגמה."
        />
      ) : (
        <GrowthChart series={data.series} />
      )}

      {data.groups.length > 0 && (
        <div className="stats-groups">
          <div className="card-h">
            <h3>לפי קבוצה</h3><span className="meta">{fmt(data.groups.length)} קבוצות</span>
          </div>
          {data.groups.map((g) => {
            const up = g.delta14d > 0;
            const dn = g.delta14d < 0;
            return (
              <div key={g.targetId} className="grp-row">
                <span className="grp-name">{g.name}</span>
                <span className="grp-bar">
                  <span style={{ width: `${(g.size / maxSize) * 100}%` }} />
                </span>
                <span className="grp-size">{fmt(g.size)}</span>
                <span className={`delta-tag ${up ? 'up' : dn ? 'dn' : 'flat'}`}>
                  {g.delta14d === 0 ? '—' : `${up ? '+' : ''}${fmt(g.delta14d)}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BroadcastsSection(): JSX.Element {
  const { data, isLoading } = useQuery({ queryKey: ['statsBroadcasts'], queryFn: () => api.statsBroadcasts() });

  if (isLoading || !data) return <div className="card"><DashboardSkeleton /></div>;

  if (data.broadcasts.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="eye"
          title="עדיין אין נתונים — יופיע לאחר השידור הראשון"
          sub="אחרי שתשלח שידור, כל הודעה תופיע כאן עם כמה קבוצות היא הגיעה אליהן וכמה אנשים באמת פתחו וקראו אותה (👀)."
        />
      </div>
    );
  }

  return (
    <>
      <div className="card bc-summary">
        <div className="bc-stat">
          <div className="k"><Icon name="eye" />סה״כ צפיות ב-{fmt(data.windowDays)} ימים</div>
          <div className="v accent">{fmt(data.totalViews)}</div>
        </div>
        <div className="bc-stat">
          <div className="k">שידורים</div>
          <div className="v">{fmt(data.broadcasts.length)}</div>
        </div>
      </div>

      <div className="bc-list">
        {data.broadcasts.map((b) => {
          const sentToTelegram = b.perGroup.some((g) => g.surface === 'telegram');
          return (
            <div key={b.campaignRunId} className="card bc-card">
              <div className="bc-title">{b.name}</div>
              {b.text && <div className="bc-text">{b.text}</div>}
              <div className="bc-time">{absTime(b.at)}</div>
              <div className="bc-chips">
                <span className="chip teal">📢 קבוצות <b>{fmt(b.groupsReached)}/{fmt(b.groupsTotal)}</b></span>
                <span className="chip green">👀 <b>{fmt(b.views)}</b> צפיות</span>
                <span className={`chip ${sentToTelegram ? 'gold' : 'muted'}`}>
                  ✈️ {sentToTelegram ? 'נשלח לטלגרם' : 'לא נשלח לטלגרם'}
                </span>
              </div>

              {b.perGroup.length > 0 && (
                <details className="bc-detail">
                  <summary>פירוט לפי קבוצה ({fmt(b.perGroup.length)})</summary>
                  <div className="bcg-row head">
                    <span>קבוצה</span>
                    <span className="bcg-num">הגיע</span>
                    <span className="bcg-num">צפיות 👀</span>
                  </div>
                  {b.perGroup.map((g, i) => (
                    <div key={i} className="bcg-row">
                      <span className="bcg-name">
                        {g.targetName} <small>· {SURFACE_LABELS[g.surface] ?? g.surface}</small>
                      </span>
                      <span className="bcg-num">{fmt(g.delivered)}</span>
                      <span className="bcg-num views">{fmt(g.views)}</span>
                    </div>
                  ))}
                </details>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
